import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowUpCircle, ShieldCheck, Bell, Check, ChevronRight, ListTodo, Search, Sigma, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTabs } from "../../contexts/TabsContext";
import { useNotifications } from "../../contexts/NotificationContext";
import { eventTypeMetaOf } from "../../constants/eventTypes";
import useNowBarPrefs, { eventScope } from "../../hooks/useNowBarPrefs";
import { subscribe as subscribeQuickFilter, getPicks, clearPicks } from "../../lib/quickFilter";

// ── The "NowBar" ─────────────────────────────────────────────────────────────
// A single pill in the top bar that surfaces whatever needs the user's attention
// RIGHT NOW — a pushed version upgrade, today's/upcoming calendar events, pending
// approvals, and unread notifications. Each source becomes an item; the leading
// chip label + icon + COLOUR change per item and the bar rotates gently through
// them. When there is nothing to show it collapses to a quiet "Nothing to update".
//
// What appears is filtered by the user's NowBar preferences (System → Settings →
// NowBar, stored per-device in localStorage — see hooks/useNowBarPrefs.js):
//   • sources — turn whole categories (updates / events / approvals / notices) on
//     or off.
//   • scopes  — which calendar events by visibility. By DEFAULT the bar shows only
//     own + shared + public events and HIDES others' private ones. Owner/developer
//     get a god-view of everyone's private events on the Calendar; without this
//     filter those buried the real (own/shared/public) items in the bar.
//
// Priority + "screen time" (how long each item holds the bar before rotating):
//   1. UPDATE  — a pushed version upgrade. Highest priority, LONGEST dwell; it
//      says "tap to refresh" and disappears on its own once the running version
//      matches (AppLayout flips `updateAvailable` back to false after a reload).
//   2. EVENTS  — today + next 7 days. Ranked by their TYPE's importance (customer
//      visit first, general last); each holds the bar for its type's screenMs, so
//      a high-importance type gets more time on screen. Colour = the type colour.
//   3. TODO    — my OPEN todos (mig 181/182). Done ones never appear: the bar is
//      finite screen time, and a finished todo has nothing left to say. Todos are
//      strictly private, so there is no scope axis — the only todos that exist for
//      you are your own. A todo marked Flicker glows AMBER (.nowbar-flicker-todo)
//      rather than the calendar's multi-hue sweep, so personal nags never read as
//      company events.
//   4. APPROVAL — pending approvals.
//   5. NOTICE  — unread notifications. LOWEST priority, SHORTEST dwell.

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Tones for the NON-event items. EVENT items take their tone from the event TYPE
// (frontend/src/constants/eventTypes.js) so the bar is the same colour as the
// event on the calendar. Same shape (pill/chip/dot/text/count) either way.
const TONE = {
  primary: {
    pill: "border-primary/30 bg-primary/10 hover:bg-primary/[0.15] dark:border-primary/30 dark:bg-primary/15",
    chip: "bg-primary text-primary-foreground",
    dot: "bg-primary",
    text: "text-foreground/90",
    count: "text-muted-foreground",
  },
  amber: {
    pill: "border-amber-200/70 bg-amber-50/80 hover:bg-amber-50 dark:border-amber-500/25 dark:bg-amber-500/[0.08] dark:hover:bg-amber-500/[0.12]",
    chip: "bg-amber-400 text-amber-950 dark:bg-amber-400/90 dark:text-amber-950",
    dot: "bg-amber-500",
    text: "text-amber-800/90 dark:text-amber-200/90",
    count: "text-amber-700/70 dark:text-amber-300/60",
  },
  muted: {
    pill: "border-border/70 bg-muted/40 hover:bg-muted/60",
    chip: "bg-muted-foreground/10 text-muted-foreground",
    dot: "bg-muted-foreground",
    text: "text-muted-foreground",
    count: "text-muted-foreground",
  },
};

// Dwell (ms) per non-event item — this is how "priority" becomes visible: more
// important → more time on screen. A version UPDATE holds longest; a notification,
// the least. EVENT items use their type's own importance-scaled screenMs.
const SCREEN_MS = { UPDATE: 20000, TODO: 7000, APPROVAL: 8000, NOTICE: 5000, EMPTY: 8000 };
const DEFAULT_MS = 6000;

// How many in-window events we hold in state, and how many reach the rotation.
// MAX_EVENT_ITEMS is applied AFTER the visibility-scope filter, never before:
// capping the RAW list first lets others' private events (which owner/developer
// receive via the god-view, then get filtered out) fill every slot and leave the
// bar empty while the user's real events sit further down the date-sorted list.
const MAX_EVENTS_HELD = 60;
const MAX_EVENT_ITEMS = 8;

// Todos reaching the rotation. Lower than events on purpose: an open todo list can
// be long, and the bar is shared with events/approvals/notices — letting 30 todos
// in would starve everything else. Flickered todos are ranked first, so the ones
// you flagged always survive this cap.
const MAX_TODO_ITEMS = 6;

// IST "today" as YYYY-MM-DD (the calendar API works in IST dates).
const istToday = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

// Whole days from a -> b for plain YYYY-MM-DD strings (UTC math, TZ-safe).
const daysBetween = (a, b) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);

const whenLabel = (days, time) => {
  if (days <= 0) return time ? `Today · ${time}` : "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
};

// Selection stats (Alt + right-click cell picks, lib/quickFilter.js). Shared
// currency symbol / unit is carried onto Sum + Avg only when EVERY numeric pick
// has it, so a mixed ₹/KG selection never shows a misleading "₹".
const fmtNum = (n) => n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
function selectionStats(picks) {
  const nums = picks.items.filter((p) => p.number !== null);
  if (!picks.items.length) return null;
  const common = (re) => {
    const found = nums.map((p) => (p.text.match(re) || [])[0]?.toUpperCase() || "");
    return found.length && found.every((f) => f && f === found[0]) ? found[0] : "";
  };
  const cur = common(/[₹$€£¥]/);
  const unit = common(/\b(kgs?|mts?|tons?|tonnes?|pcs?|nos?|units?|hrs?|days?|%)(?=\s*$)/i);
  const fmt = (n) => `${cur}${fmtNum(n)}${unit ? ` ${unit}` : ""}`;
  const sum = nums.reduce((a, p) => a + p.number, 0);
  return {
    count: picks.items.length,
    numeric: nums.length,
    sum: nums.length ? fmt(sum) : "—",
    avg: nums.length ? fmt(sum / nums.length) : "—",
    column: picks.sameColumn !== null ? picks.items[0].label : "",
  };
}

const NowBar = ({ pendingApprovalsCount = 0, updateAvailable = false, latestVersion = "", className = "", compact = false }) => {
  const navigate = useNavigate();
  const { tabs, navigateFrame, activateTab, openInNewTab } = useTabs();
  const { token, user } = useAuth();
  const { notifications, unreadCount } = useNotifications();
  const prefs = useNowBarPrefs();
  const [events, setEvents] = useState([]);
  const [todos, setTodos] = useState([]);
  // A live cell selection takes over the bar (Excel-style status readout).
  const [selection, setSelection] = useState(() => selectionStats(getPicks()));
  useEffect(() => subscribeQuickFilter(() => setSelection(selectionStats(getPicks()))), []);

  // Global search: the search icon turns the message area into a text box.
  // Submitting opens the /search results page (one reusable "Search" tab).
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef(null);
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);
  const closeSearch = () => {
    setSearchOpen(false);
    setSearchText("");
  };
  const submitSearch = (e) => {
    e.preventDefault();
    const q = searchText.trim();
    if (q.length < 2) return;
    const href = `/search?q=${encodeURIComponent(q)}`;
    // Reuse an existing Search tab instead of stacking a new one per query.
    const existing = tabs.find((t) => (t.href || "").split("?")[0] === "/search");
    if (existing) {
      navigateFrame(existing.id, href);
      // After the tab's href updates, switch to it (activateTab navigates to it).
      setTimeout(() => activateTab(existing.id), 0);
    } else {
      openInNewTab(href, "Search");
    }
    closeSearch();
  };

  // Keep the bar LIVE — no page refresh needed. Today's events + a rolling 7-day
  // window are re-pulled: on a short interval (for other users' / other tabs'
  // changes), whenever the tab regains focus/visibility, and INSTANTLY in this tab
  // when a calendar event is added/edited/deleted (CalendarPage fires the
  // `calendar:events-changed` window event on every successful mutation).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const today = istToday();
        const to = new Date(Date.parse(`${today}T00:00:00Z`) + 7 * 86400000)
          .toISOString()
          .slice(0, 10);
        const res = await axios.get(`${API}/calendar/events`, {
          params: { from: today, to },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const t = istToday();
        // Hold every in-window event; the scope filter, ranking and display cap
        // all happen at render time (see below) so a prefs change applies
        // instantly and the cap only ever counts events the user can actually see.
        const picked = (res.data || [])
          .filter((e) => !e.done && e.event_date)
          .map((e) => ({ e, days: daysBetween(t, e.event_date) }))
          .filter(({ days }) => days >= 0 && days <= 7)
          .slice(0, MAX_EVENTS_HELD);
        setEvents(picked);
      } catch {
        if (!cancelled) setEvents([]);
      }
    };
    load();
    const id = setInterval(load, 30 * 1000); // live-ish poll for cross-user changes
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    const onChanged = () => load(); // same-tab instant update after a mutation
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("calendar:events-changed", onChanged);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("calendar:events-changed", onChanged);
    };
  }, [token]);

  // My OPEN todos (mig 181/182). Same live-refresh contract as events: poll, refresh
  // on focus/visibility, and instantly in this tab when TodoPage mutates something
  // (it fires `todos:changed` on every successful add/tick/rename/delete).
  // GET /api/todos is already hard-scoped to the caller server-side, so there is
  // nothing to filter for privacy here — only `done` is dropped.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/todos`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setTodos((res.data || []).filter((t) => !t.done));
      } catch {
        if (!cancelled) setTodos([]);
      }
    };
    load();
    const id = setInterval(load, 30 * 1000);
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    const onChanged = () => load();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("todos:changed", onChanged);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("todos:changed", onChanged);
    };
  }, [token]);

  // Build the rotation. Each item carries its own tone / icon / label / dwell so
  // colour + screen time follow priority (and, for events, the type importance).
  // Whole categories are gated by prefs.sources; events are additionally filtered
  // to the visibility scopes the user kept on (prefs.scopes).
  const items = [];
  // 1 — a pushed version upgrade the user hasn't refreshed into yet.
  if (updateAvailable && prefs.sources.updates) {
    items.push({
      key: "update",
      label: "UPDATE",
      Icon: ArrowUpCircle,
      tone: TONE.primary,
      screenMs: SCREEN_MS.UPDATE,
      showDot: true,
      text: `New version ${latestVersion || ""} available — tap to refresh`.replace("  ", " "),
      onClick: () => window.location.reload(),
    });
  }
  // 2 — events. Filtered to the user's chosen visibility scopes FIRST (so others'
  // private events from the owner/developer god-view can't bury or crowd out real
  // ones), then ranked: flickered events first, then type importance, then soonest.
  // Only after that is the display cap applied. Colour + dwell come from the type;
  // the description (notes) is appended after the "when" label.
  if (prefs.sources.events) {
    events
      .filter(({ e }) => prefs.scopes[eventScope(e, user?.id)])
      // `flick` mirrors the render gate: an event only jumps the queue when it
      // will actually flicker for this viewer, so turning Flicker off leaves the
      // previous ordering completely untouched.
      .map(({ e, days }) => ({ e, days, tm: eventTypeMetaOf(e.event_type), flick: prefs.flicker && !!e.flicker }))
      .sort((a, b) =>
        Number(b.flick) - Number(a.flick)
        || b.tm.importance - a.tm.importance
        || a.days - b.days)
      .slice(0, MAX_EVENT_ITEMS)
      .forEach(({ e, days, tm }) => {
        const note = (e.notes || "").trim();
        items.push({
          key: `event-${e.id}`,
          label: tm.label.toUpperCase(),
          Icon: tm.Icon,
          tone: tm.notify,
          screenMs: tm.screenMs,
          showDot: true,
          // Marked Flicker on the event (mig 180) → the pill flickers while this
          // item is on screen, but only if the viewer opted into the effect.
          flicker: !!e.flicker,
          text: `${e.title} — ${whenLabel(days, e.event_time)}${note ? ` · ${note}` : ""}`,
          onClick: () => navigate("/calendar"),
        });
      });
  }
  // 3 — my open todos. Ranked flickered-first (mirroring the events rule, so a
  // flagged todo survives the cap), then newest first. No scope filter: todos are
  // private by construction, so every todo here is already mine.
  if (prefs.sources.todos) {
    todos
      .map((t) => ({ t, flick: prefs.flicker && !!t.flicker }))
      .sort((a, b) =>
        Number(b.flick) - Number(a.flick)
        || String(b.t.created_at || "").localeCompare(String(a.t.created_at || "")))
      .slice(0, MAX_TODO_ITEMS)
      .forEach(({ t }) => {
        items.push({
          key: `todo-${t.id}`,
          label: "TODO",
          Icon: ListTodo,
          tone: TONE.amber,
          screenMs: SCREEN_MS.TODO,
          showDot: true,
          flicker: !!t.flicker,
          // Amber single-hue glow instead of the calendar's colour sweep, so a
          // personal todo never reads as a company event.
          flickerClass: "nowbar-flicker-todo",
          text: `${t.title}${t.notify ? " · reminding you" : ""}`,
          onClick: () => navigate("/todos"),
        });
      });
  }
  // 4 — pending approvals.
  if (pendingApprovalsCount > 0 && prefs.sources.approvals) {
    items.push({
      key: "approvals",
      label: "APPROVAL",
      Icon: ShieldCheck,
      tone: TONE.amber,
      screenMs: SCREEN_MS.APPROVAL,
      showDot: true,
      text: `${pendingApprovalsCount} approval${pendingApprovalsCount > 1 ? "s" : ""} pending your review`,
      onClick: () => navigate("/system/approvals"),
    });
  }
  // 5 — unread notifications (lowest priority, shortest dwell).
  if (unreadCount > 0 && prefs.sources.notifications) {
    const latest = (notifications || []).find((n) => !n.read);
    const title = (latest?.title || latest?.body || "").trim();
    items.push({
      key: "notifications",
      label: "NOTICE",
      Icon: Bell,
      tone: TONE.amber,
      screenMs: SCREEN_MS.NOTICE,
      showDot: true,
      text: unreadCount === 1 && title ? title : `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}`,
      onClick: () => navigate("/system/notifications"),
    });
  }

  const EMPTY_ITEM = {
    key: "empty", label: "CLEAR", Icon: Check, tone: TONE.muted,
    screenMs: SCREEN_MS.EMPTY, showDot: false, text: "Nothing to update", onClick: null,
  };
  const displayItems = items.length ? items : [EMPTY_ITEM];

  // Gentle rotation with a per-item dwell (screenMs); pauses on hover. itemsRef
  // keeps the timer reading the live item without re-arming on every render.
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const itemsRef = useRef(displayItems);
  itemsRef.current = displayItems;
  useEffect(() => {
    setIdx(0);
  }, [displayItems.length]);
  useEffect(() => {
    if (displayItems.length <= 1 || paused || searchOpen) return;
    const cur = itemsRef.current[Math.min(idx, itemsRef.current.length - 1)];
    const ms = (cur && cur.screenMs) || DEFAULT_MS;
    const id = setTimeout(() => setIdx((i) => (i + 1) % itemsRef.current.length), ms);
    return () => clearTimeout(id);
  }, [idx, paused, searchOpen, displayItems.length]);

  const active = displayItems[Math.min(idx, displayItems.length - 1)];
  const tone = active.tone;
  const Icon = active.Icon;
  const clickable = !!active.onClick;
  // Both switches must be on: the viewer's Flicker setting AND this item's own
  // Flicker flag. Drives the glow + sheen defined in index.css. Todos bring their
  // own amber variant via flickerClass; calendar events use the default sweep.
  const flickering = prefs.flicker && !!active.flicker;
  const flickerClass = active.flickerClass || "nowbar-flicker";
  // Show the "next" control whenever the bar holds at least one REAL update
  // (not the "Nothing to update" fallback). The position counter (1/N) only
  // makes sense with 2+, so it's gated separately.
  const hasItems = items.length > 0;
  const hasMultiple = displayItems.length > 1;
  // Jump to the next item immediately, without waiting out the current dwell.
  // Changing `idx` re-arms the rotation timer with the new item's screenMs.
  const showNext = () => setIdx((i) => (i + 1) % itemsRef.current.length);

  // Search mode — the pill becomes a search box. The input slides in from the
  // search icon's side; Esc / X / blur-with-empty-text slides back to the updates.
  if (searchOpen && !compact) {
    return (
      <form
        data-testid="now-bar-search-form"
        onSubmit={submitSearch}
        className={`relative h-8 flex items-center overflow-hidden rounded-full border transition-colors ring-2 ring-primary/15 ${TONE.primary.pill} ${className}`}
      >
        <span className={`flex items-center gap-1.5 self-stretch pl-4 pr-3 animate-in fade-in zoom-in-95 duration-300 ${TONE.primary.chip}`}>
          <Search className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="text-[11px] font-heading font-bold uppercase tracking-wide hidden md:inline">Search</span>
        </span>
        <input
          ref={searchInputRef}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") closeSearch(); }}
          onBlur={() => { if (!searchText.trim()) closeSearch(); }}
          placeholder="Search the whole ERP — customers, orders, invoices, heats…"
          aria-label="Search the ERP"
          maxLength={80}
          className="flex-1 min-w-0 h-full bg-transparent px-3 text-xs font-medium text-foreground placeholder:text-muted-foreground/70 outline-none animate-in fade-in slide-in-from-right-6 duration-300"
        />
        {searchText.trim().length >= 2 && (
          <span className="hidden sm:flex items-center gap-1 pr-2 text-[10px] text-muted-foreground animate-in fade-in duration-200">
            <kbd className="rounded border border-border bg-background px-1 font-sans">Enter</kbd>
          </span>
        )}
        <button
          type="button"
          title="Close search (Esc)"
          aria-label="Close search"
          // mousedown fires before the input's blur, so the click isn't lost.
          onMouseDown={(e) => e.preventDefault()}
          onClick={closeSearch}
          className={`flex-shrink-0 self-stretch flex items-center px-3 border-l border-black/5 dark:border-white/10 cursor-pointer transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06] ${TONE.primary.count}`}
        >
          <X className="w-3.5 h-3.5 animate-in spin-in-90 duration-300" />
        </button>
      </form>
    );
  }

  // Cell selection active → show Count · Sum · Avg instead of the rotation.
  // Not in compact mode (Docs corner has no room and no data-tables).
  if (selection && !compact) {
    const stat = (k, v) => (
      <span className="whitespace-nowrap">
        <span className="text-muted-foreground font-normal">{k} </span>
        <span className="font-semibold tabular-nums text-foreground">{v}</span>
      </span>
    );
    const title = `${selection.column ? `${selection.column} · ` : ""}Count ${selection.count} · Sum ${selection.sum} · Avg ${selection.avg}`;
    return (
      <div
        data-testid="now-bar-selection"
        title={title}
        className={`relative h-8 flex items-center overflow-hidden rounded-full border transition-colors ${TONE.primary.pill} ${className}`}
      >
        <span className={`flex items-center gap-1.5 self-stretch pl-4 pr-3 ${TONE.primary.chip}`}>
          <Sigma className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="text-[11px] font-heading font-bold uppercase tracking-wide hidden md:inline">Selection</span>
        </span>
        <span className="flex-1 min-w-0 flex items-center gap-3 px-3 text-xs truncate animate-in fade-in duration-200">
          {selection.column && (
            <span className="text-muted-foreground uppercase tracking-wide text-[10px] truncate max-w-[140px]">{selection.column}</span>
          )}
          {stat("Count", selection.count)}
          {stat("Sum", selection.sum)}
          {stat("Avg", selection.avg)}
          {selection.numeric < selection.count && (
            <span className="text-muted-foreground text-[10px] whitespace-nowrap">({selection.count - selection.numeric} non-numeric)</span>
          )}
        </span>
        <button
          type="button"
          title="Clear selection (Esc)"
          aria-label="Clear selection"
          onClick={() => clearPicks()}
          className="flex-shrink-0 self-stretch flex items-center px-3 border-l border-black/5 dark:border-white/10 text-muted-foreground hover:text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // Compact mode — a small stack of overlapping round icon badges (no label/
  // text), for spots too narrow for the full pill (e.g. the Docs workspace
  // brand corner, which has no equivalent of AppLayout's iconControls
  // capsule). Every current item gets its own circle, overlapped like an
  // avatar stack. Purely informational — nothing here navigates — so each
  // circle is inert and only reveals its detail as a small card that grows
  // out on hover (desktop mouse only; there is no click/tap affordance).
  // Capped so the stack never crowds out the brand it sits next to; anything
  // beyond the cap collapses into a trailing "+N" circle.
  if (compact) {
    const MAX_COMPACT_ITEMS = 4;
    const shown = displayItems.slice(0, MAX_COMPACT_ITEMS);
    const overflow = Math.max(0, items.length - shown.length);
    return (
      <div className={`flex items-center flex-shrink-0 ${className}`}>
        {shown.map((item, i) => {
          const t = item.tone;
          const ItemIcon = item.Icon;
          const isFlickering = prefs.flicker && !!item.flicker;
          const itemFlickerClass = item.flickerClass || "nowbar-flicker";
          return (
            <div
              key={item.key}
              data-testid="now-bar-compact-item"
              style={{ zIndex: shown.length - i }}
              className={`group relative ${i > 0 ? "-ml-2.5" : ""}`}
            >
              <div
                aria-hidden="true"
                className={`relative flex items-center justify-center w-6 h-6 rounded-full ring-2 ring-background shadow-sm transition-all cursor-default group-hover:z-20 group-hover:scale-110 group-hover:shadow-md ${t.chip} ${isFlickering ? itemFlickerClass : ""}`}
              >
                {isFlickering && <span aria-hidden className="nowbar-flicker-sheen rounded-full" />}
                <ItemIcon className="w-3 h-3" />
                {item.showDot && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${t.dot}`} />
                    <span className={`relative inline-flex h-2 w-2 rounded-full ring-[1.5px] ring-background ${t.dot}`} />
                  </span>
                )}
              </div>

              {/* Hover-reveal detail card — grows out from under the circle,
                  never intercepts pointer events so it can't be mistaken for
                  a click target. */}
              <div
                role="tooltip"
                className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-max max-w-[220px] -translate-x-1/2 translate-y-1 origin-top scale-95 opacity-0 transition-all duration-150 group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100"
              >
                <div className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 rounded-[2px] border-l border-t border-border bg-popover" />
                <div className="relative flex items-start gap-1.5 rounded-lg border border-border bg-popover px-2.5 py-1.5 shadow-lg">
                  <span className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full ${t.chip}`}>
                    <ItemIcon className="w-2.5 h-2.5" />
                  </span>
                  <span className="text-[11px] font-medium leading-snug text-popover-foreground">{item.text}</span>
                </div>
              </div>
            </div>
          );
        })}
        {overflow > 0 && (
          <span
            title={`${overflow} more update${overflow > 1 ? "s" : ""}`}
            className="relative -ml-2.5 flex items-center justify-center w-6 h-6 rounded-full ring-2 ring-background shadow-sm bg-muted-foreground/15 text-muted-foreground text-[9px] font-semibold cursor-default"
          >
            +{overflow}
          </span>
        )}
      </div>
    );
  }

  // The pill is a container (NOT a button) so it can hold two independent
  // controls: the main action (navigate / refresh for the shown item) and a
  // "next" control that lets the user step through everything without waiting.
  return (
    <div
      data-testid="now-bar"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className={`group relative h-8 flex items-center gap-0 overflow-hidden rounded-full border transition-colors ${tone.pill} ${flickering ? flickerClass : ""} ${className}`}
    >
      {flickering && <span aria-hidden className="nowbar-flicker-sheen" />}
      <button
        type="button"
        data-testid="now-bar-action"
        title={active.text}
        onClick={active.onClick || undefined}
        className={`flex-1 min-w-0 self-stretch flex items-center gap-0 ${clickable ? "cursor-pointer" : "cursor-default"}`}
      >
        <span className={`flex items-center gap-1.5 self-stretch pl-4 pr-3 ${tone.chip}`}>
          <Icon className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="text-[11px] font-heading font-bold uppercase tracking-wide hidden md:inline">
            {active.label}
          </span>
        </span>

        {active.showDot && (
          <span className="relative flex h-1.5 w-1.5 flex-shrink-0 mx-3">
            <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 motion-safe:animate-ping ${tone.dot}`} />
            <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${tone.dot}`} />
          </span>
        )}

        <span className={`flex-1 min-w-0 pr-3 ${active.showDot ? "" : "pl-4"}`}>
          <span
            key={active.key}
            className={`block truncate text-left text-xs font-medium animate-in fade-in duration-500 ${tone.text}`}
          >
            {active.text}
          </span>
        </span>
      </button>

      {hasItems && (
        <button
          type="button"
          data-testid="now-bar-next"
          title="Show next update"
          aria-label="Show next update"
          onClick={showNext}
          className={`flex-shrink-0 self-stretch flex items-center gap-1 pl-2.5 pr-3 border-l border-black/5 dark:border-white/10 cursor-pointer transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06] ${tone.count}`}
        >
          {hasMultiple && (
            <span className="text-[10px] font-semibold tabular-nums">
              {idx + 1}/{displayItems.length}
            </span>
          )}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Search — opens the global search box in place of the message area. */}
      <button
        type="button"
        data-testid="now-bar-search"
        title="Search the ERP"
        aria-label="Search the ERP"
        onClick={() => setSearchOpen(true)}
        className={`flex-shrink-0 self-stretch flex items-center px-3 border-l border-black/5 dark:border-white/10 cursor-pointer transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06] ${tone.count}`}
      >
        <Search className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default NowBar;
